<script lang="ts">
  import TextInput from '$lib/components/paywall/TextInput.svelte';
  import { getPromoCode } from '$lib/gateway/stripeClient';
  import type { GetPromoCodeResponse } from '../../../routes/api/stripe/promo/[code]/+server';
  import Coupon from '../../../assets/svgs/coupon.svelte';
  import { onMount } from 'svelte';
  import { queryParam } from 'sveltekit-search-params';
  import { couponDescription, validCoupon } from '$lib/helpers/couponHelpers';
  import { userStore } from '$lib/stores/userStore';

  const promoCode = queryParam('promo_code');

  export let coupon: GetPromoCodeResponse | undefined = undefined;
  export let appliedCouponCode: string | undefined = undefined;
  export let price;

  let promoCodeInput: string = $promoCode || '';
  let promoError: string | undefined = undefined;

  let oldPrice = undefined;
  let editable: boolean = true;

  async function applyPromoCode() {
    const trimmedPromoCode = promoCodeInput.trim();
    if (!trimmedPromoCode || trimmedPromoCode === '') return ($promoCode = null);

    const { data, error } = await getPromoCode(trimmedPromoCode);

    if (error) {
      if (appliedCouponCode) {
        appliedCouponCode = undefined;
      }

      coupon = undefined;
      return (promoError = error);
    }

    $userStore.promo_code = trimmedPromoCode;

    promoError = undefined;
    $promoCode = trimmedPromoCode;
    coupon = data;
    await validateCoupon(coupon, price);
  }

  async function handleRemove() {
    coupon = undefined;
    $promoCode = null;
    $userStore.promo_code = null;
  }

  onMount(() => {
    if (appliedCouponCode) {
      promoCodeInput = appliedCouponCode;
      applyPromoCode();
    }
  });

  let promotion = '',
    duration = '';

  $: {
    if (price != oldPrice) {
      // Apply promotion code from plan
      const code = price?.promotionCode || appliedCouponCode;

      if (code) {
        promoCodeInput = code;
        promoError = undefined;
        applyPromoCode();
      } else if (!price?.promotionCode && oldPrice?.promotionCode == promoCodeInput) {
        // Clear promo code input when user select other plan without promotion code
        promoCodeInput = '';
        handleRemove();
      }
      // Disable promo code input when plan has promotion code
      editable = !price?.promotionCode;
    }

    if (coupon) {
      promotion = couponDescription(coupon, undefined, undefined, price.currency);
      validateCoupon(coupon, price);
    } else if (oldPrice && oldPrice.id != price.id) {
      // Clear coupon error message when user select other plan
      promoError = undefined;
    }

    oldPrice = price;
  }

  async function validateCoupon(coupon, price) {
    if (!validCoupon(coupon, price) && !price?.promotionCode) {
      promoError = `This coupon can't be applied to the plan ${price.label}.`;
      await handleRemove();
    }
  }
</script>

<div>
  <span class="text-gray-900 text-sm font-medium mb-2">Promo Code</span>
  {#if coupon}
    <div class="flex items-center justify-between gap-4 px-4 py-6 bg-gray-100">
      <div class="flex items-center gap-4">
        <Coupon customClass="text-gray-900" />

        <div class="flex flex-col">
          <span class="text-sm font-medium" data-coupon={coupon.code}>
            {coupon.code} <span class="font-normal">applied</span>
          </span>
          <span class="text-sm text-gray-500" data-discount={promotion}>
            {promotion}
          </span>
        </div>
      </div>

      <span
        hidden={!editable}
        on:click={handleRemove}
        on:keydown={handleRemove}
        role="button"
        tabindex="0"
        class="text-primary-500 text-sm font-medium">Remove</span
      >
    </div>
  {:else}
    <div class="flex items-start gap-2 w-full">
      <div class="flex-1">
        <TextInput
          bind:value={promoCodeInput}
          label="Enter promo code"
          animateLabel={false}
          error={promoError}
        />
      </div>
      <button
        class="plausible-event-name=new_paywall_add_coupon text-gray-700 font-semibold py-2.5 px-6 rounded-full border border-gray-500 mt-1"
        on:click={applyPromoCode}>Apply</button
      >
    </div>
  {/if}
</div>
